import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IRequirementApplicabilityConditionDetail,
  IRequirementAuthoringOptions,
  RequirementApplicabilityConditionRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ensureOk from "./ensureOk";
import { OperatorTypeValue, selectedEnumValue } from "./lookupEnums";

type ConditionValues = {
  conditionTypeId?: string;
  operatorTypeId?: string;
  referenceValue?: string;
  rangeEndValue?: string;
};

const { Form } = withTypes<ConditionValues>();

type ConditionDialogProps = {
  requirementId: string;
  condition?: IRequirementApplicabilityConditionDetail;
  options?: IRequirementAuthoringOptions;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const ConditionDialog = ({
  requirementId,
  condition,
  options,
  open,
  onClose,
  onSaved,
}: ConditionDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementService } = useGlobalContext();

  const handleSubmit = async (values: ConditionValues) => {
    try {
      const operator = selectedEnumValue(
        options?.operatorTypes,
        values.operatorTypeId
      );

      const response = await requirementService.saveApplicabilityCondition(
        RequirementApplicabilityConditionRequest.fromJS({
          id: condition?.id,
          requirementId,
          conditionTypeId: values.conditionTypeId,
          operatorTypeId: values.operatorTypeId,
          referenceValue: values.referenceValue,
          // A range end only means anything for the in-range operator.
          rangeEndValue:
            operator === OperatorTypeValue.InRange
              ? values.rangeEndValue || undefined
              : undefined,
        })
      );

      ensureOk(response);
      onSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          conditionTypeId:
            condition?.conditionTypeId ?? options?.conditionTypes?.[0]?.id ?? "",
          operatorTypeId:
            condition?.operatorTypeId ?? options?.operatorTypes?.[0]?.id ?? "",
          referenceValue: condition?.referenceValue ?? "",
          rangeEndValue: condition?.rangeEndValue ?? "",
        }}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const operator = selectedEnumValue(
            options?.operatorTypes,
            values.operatorTypeId
          );

          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogTitle>
                {t({
                  id: condition ? "editApplicabilityCondition" : "addApplicabilityCondition",
                })}
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="conditionTypeId">
                    {({ input }) => (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          variant="standard"
                          fullWidth
                          label={t({ id: "conditionType" })}
                          {...input}
                        >
                          {(options?.conditionTypes ?? []).map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Field>

                  <Field name="operatorTypeId">
                    {({ input }) => (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          variant="standard"
                          fullWidth
                          label={t({ id: "operatorLabel" })}
                          {...input}
                        >
                          {(options?.operatorTypes ?? []).map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Field>

                  <Field name="referenceValue" required>
                    {({ input, meta }) => (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          variant="standard"
                          fullWidth
                          label={t({ id: "referenceValue" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {operator === OperatorTypeValue.InRange && (
                    <Field name="rangeEndValue">
                      {({ input }) => (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "rangeEndValue" })}
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>
                  )}

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button disabled={submitting} onClick={onClose}>
                  {t({ id: "cancelButton" })}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: condition ? "saveButton" : "addCondition" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

export default ConditionDialog;
